












import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { mapGetters, mapState } from 'vuex';

import { initAnalytics } from 'frontend/utils/analytics';
import { meta } from 'frontend/utils/decorators';
import {
  ClientType,
  ExperimentGetter,
  FeatureSwitches,
  MobileGetter,
  Optional,
  User,
} from 'frontend/types';
import { isActuallyNux } from 'frontend/utils/nux';

import LoadingAnimation from 'frontend/components/LoadingAnimation.vue';

@Component({
  components: {
    LoadingAnimation,
    AppDesktop: () =>
      // tslint:disable-next-line:space-in-parens
      import(/* webpackChunkName: 'AppDesktop' */ 'frontend/views/AppDesktop.vue'),
    AppMobile: () =>
      // tslint:disable-next-line:space-in-parens
      import(/* webpackChunkName: 'AppMobile' */ 'frontend/views/AppMobile.vue'),
    // We need this here in case something happens when loading the initial data
    ErrorModal: () =>
      // tslint:disable-next-line:space-in-parens
      import(/* webpackChunkName: 'ErrorModal' */ 'frontend/components/ErrorModal.vue'),
  },
  computed: {
    ...mapGetters('pageHeader', ['clientType', 'isMobile']),
    ...mapGetters('response', ['requestPath']),
    ...mapGetters('globals', ['inExperiment', 'featureSwitches']),
    ...mapState('errorModal', ['errorModalIsVisible']),
    ...mapState('globals', ['user']),
  },
})
export default class App extends Vue {
  private clientType!: ClientType;

  private requestPath!: string;

  private user!: User;

  private isMobile!: MobileGetter;

  private inExperiment!: ExperimentGetter;

  private readonly featureSwitches!: FeatureSwitches;

  @meta
  private metaInfo() {
    const path = this.requestPath ? this.requestPath.split('?')[0] : this.$route.path;
    return {
      titleTemplate: (title: string | undefined) => (title ? `${title} | Brilliant` : 'Brilliant'),
      meta: [
        { vmid: 'al:ios:url', property: 'al:ios:url', content: `com.brilliant.Brilliant:/${path}` },
        {
          vmid: 'al:android:url',
          property: 'al:android:url',
          content: `https://brilliant.org${path}`,
        },
      ],
      link: [
        {
          vmid: 'canonical',
          rel: 'canonical',
          href: `https://brilliant.org${path}`,
        },
        {
          vmid: 'alternate',
          rel: 'alternate',
          href: `android-app://org.brilliant.android/http/brilliant.org${path}`,
        },
      ],
    };
  }

  private created() {
    this.$store.dispatch('banner/prefetchBanner', {
      routePath: this.$route.path,
      q: this.$route.query,
    });
  }

  private mounted(): void {
    // this initAnalytics is for first page load
    initAnalytics(this.$store);
  }

  private get mobile(): boolean {
    return this.isMobile(this.$mq);
  }

  private get shouldHideHeader(): boolean {
    // Header can be hidden in following ways:
    // 1. define and enable feature switch in $route.meta.hideHeaderSwitch (conditionally)
    // 2. enable $route.meta.hideHeader flag
    // 3. define a query param in $route.meta.hideHeaderParam, where if it exists we hide the header
    const hideHeaderBasedOnSwitch = this.$route.meta.hideHeaderSwitch as Optional<string>;
    const hideHeaderBasedOnParam = this.$route.meta.hideHeaderParam as Optional<{
      key: string;
      value: string;
    }>;
    if (!!hideHeaderBasedOnSwitch) {
      return (
        hideHeaderBasedOnSwitch in this.featureSwitches &&
        // @ts-expect-error
        this.featureSwitches[hideHeaderBasedOnSwitch] === true
      );
    }
    if (!!hideHeaderBasedOnParam) {
      return this.$route.query?.[hideHeaderBasedOnParam.key] === hideHeaderBasedOnParam.value;
    }
    return this.$route.meta.hideHeader;
  }

  private checkHash(): void {
    const { hash } = this.$route;
    if (hash) {
      const targetElement = document.getElementById(hash.replace('#', ''));
      if (targetElement) {
        window.scroll(0, targetElement.offsetTop);
      }
    }
  }

  // this initAnalytics is for when the route changes
  @Watch('$route')
  private onRouteChanged(to: Route, from: Route) {
    initAnalytics(this.$store, true);
    if (from?.name !== to.name) {
      // do not trigger for route params changes, (eg  course chapter change in lesson view)
      this.$store.dispatch('banner/invalidateBannerCache', {
        routePath: from.path,
      });
    }
  }
}
